import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';

// Прямым путём, не через барабан `core/hooks`: окно задачи и окно создания
// живут на каждом экране доски, а барабан тянет инициализацию всех доменов.
import {
  ITaskSmartLink,
  useTaskSmartLinks,
} from 'core/hooks/Students/useTaskSmartLinks';
import { TaskLinkInput } from 'gqlhooks';

import { IStaffTask } from './taskBuckets';
import { IPickedLink } from './TaskLinkPicker';
import { linkIcon } from './taskLinkKinds';
import { linkKindTone } from './taskTones';
import * as M from './TaskModal.styled';
import * as L from './TaskList.styled';

const keyOf = (link: { kind: string; id: string }) => `${link.kind}:${link.id}`;

const SuggestionBody = ({ link }: { link: ITaskSmartLink }) => {
  const Icon = linkIcon(link.kind);
  return (
    <>
      <Icon size={12} strokeWidth={2} aria-hidden="true" />
      <span>{link.label}</span>
      {link.hint ? <M.SmartHint>{link.hint}</M.SmartHint> : null}
    </>
  );
};

interface ITaskSmartLinkChipsProps {
  /** Набранное название. */
  title: string;
  /** Связи формы создания. */
  links: IPickedLink[];
  onLinks: (next: IPickedLink[]) => void;
  /** См. `ITitleAddonContext.registerSettle` — «дожать» перед созданием. */
  registerSettle?: (fn: (() => Promise<IPickedLink[]>) | null) => void;
}

/**
 * Умные чипы связей в окне СОЗДАНИЯ (ТЗ 0109).
 *
 * «Позвонить PY-55 Kowalski» → группа PY-55, студент, его сделка. Уверенные
 * совпадения прицепляются САМИ — по одному разу на подсказку: снятая крестиком
 * связь в этой сессии окна обратно не возвращается, иначе человек воевал бы с
 * формой. Неуверенные (токен совпал с несколькими) — серые «Связать?»: их
 * добавляет только клик.
 */
export const TaskSmartLinkChips = memo(
  ({ title, links, onLinks, registerSettle }: ITaskSmartLinkChipsProps) => {
    const { t } = useTranslation();
    const { links: suggestions, settled, fetchNow } = useTaskSmartLinks(title);
    // Подсказки, которые уже прошли через авто-добавление (или были в
    // связях к моменту прихода) — второй раз сами не добавляются.
    const handled = useRef(new Set<string>());
    /**
     * Всё, что пришло из подсказок и было прицеплено (само или кликом).
     *
     * 🔴 Ревью 0109: дописали название — подсказка исчезла из ответа, а
     * связь, которую она прицепила, осталась в форме БЕЗ чипа. Ошибочную
     * связь нечем было снять. Теперь чип живёт, пока связь в форме.
     */
    const [adopted, setAdopted] = useState<Map<string, ITaskSmartLink>>(
      () => new Map(),
    );
    const linksRef = useRef(links);
    linksRef.current = links;
    const titleRef = useRef(title);
    titleRef.current = title;
    const settledRef = useRef(settled);
    settledRef.current = settled;
    const suggestionsRef = useRef(suggestions);
    suggestionsRef.current = suggestions;

    const adopt = useCallback((items: ITaskSmartLink[]) => {
      setAdopted((prev) => {
        const next = new Map(prev);
        items.forEach((item) => next.set(keyOf(item), item));
        return next;
      });
    }, []);

    /**
     * Перед созданием (ревью 0109): если подсказки для набранного названия
     * ещё не пришли — спросить СЕЙЧАС, мимо паузы, и прицепить уверенные,
     * которых человек не снимал. Отдаёт итоговые связи формы.
     */
    const settle = useCallback(async (): Promise<IPickedLink[]> => {
      const list = settledRef.current
        ? suggestionsRef.current
        : await fetchNow(titleRef.current);
      const { current } = linksRef;
      const has = new Set(current.map(keyOf));
      const fresh = list.filter(
        (s) => s.confident && !handled.current.has(keyOf(s)),
      );
      fresh.forEach((s) => handled.current.add(keyOf(s)));
      const toAdd = fresh.filter((s) => !has.has(keyOf(s)));
      if (!toAdd.length) return current;
      adopt(toAdd);
      const next = [
        ...current,
        ...toAdd.map((s) => ({ kind: s.kind, id: s.id, name: s.label })),
      ];
      onLinks(next);
      return next;
    }, [adopt, fetchNow, onLinks]);

    useEffect(() => {
      if (!registerSettle) return undefined;
      registerSettle(settle);
      return () => registerSettle(null);
    }, [registerSettle, settle]);

    useEffect(() => {
      const { current } = linksRef;
      const has = new Set(current.map(keyOf));
      const fresh = suggestions.filter(
        (s) => s.confident && !handled.current.has(keyOf(s)),
      );
      fresh.forEach((s) => handled.current.add(keyOf(s)));
      const toAdd = fresh.filter((s) => !has.has(keyOf(s)));
      if (toAdd.length) {
        adopt(toAdd);
        onLinks([
          ...current,
          ...toAdd.map((s) => ({ kind: s.kind, id: s.id, name: s.label })),
        ]);
      }
    }, [suggestions, onLinks, adopt]);

    const linked = new Set(links.map(keyOf));
    const shown = new Set(suggestions.map(keyOf));
    const items: ITaskSmartLink[] = [
      ...suggestions,
      ...Array.from(adopted.values()).filter(
        (item) => linked.has(keyOf(item)) && !shown.has(keyOf(item)),
      ),
    ];
    if (!items.length) return null;
    const attached = items.filter((item) => linked.has(keyOf(item)));

    return (
      <M.SmartRow>
        <M.SmartLabel>{t('pages.tasks.smart.label')}</M.SmartLabel>
        {items.map((s) => {
          const name = s.hint ? `${s.label} · ${s.hint}` : s.label;
          if (linked.has(keyOf(s))) {
            return (
              <M.SmartChip
                key={keyOf(s)}
                $tone={linkKindTone(s.kind)}
                title={`${t(`pages.tasks.links.kind.${s.kind}`)}: ${name}`}
              >
                <SuggestionBody link={s} />
                <M.SmartRemove
                  type="button"
                  aria-label={t('pages.tasks.smart.remove', { name: s.label })}
                  onClick={() =>
                    onLinks(links.filter((l) => keyOf(l) !== keyOf(s)))
                  }
                >
                  <X size={12} aria-hidden="true" />
                </M.SmartRemove>
              </M.SmartChip>
            );
          }
          return (
            <M.SmartChip
              key={keyOf(s)}
              title={`${t(`pages.tasks.links.kind.${s.kind}`)}: ${name}`}
            >
              <M.SmartButton
                type="button"
                aria-label={t('pages.tasks.smart.linkWith', { name })}
                onClick={() => {
                  adopt([s]);
                  onLinks([
                    ...links,
                    { kind: s.kind, id: s.id, name: s.label },
                  ]);
                }}
              >
                <Plus size={12} aria-hidden="true" />
                {t('pages.tasks.smart.suggest')}
                <SuggestionBody link={s} />
              </M.SmartButton>
            </M.SmartChip>
          );
        })}
        {/* Что прицепилось само — читалке словами: чип появился без
            действия человека, и иначе он о нём не узнал бы. */}
        <L.SrOnly role="status" aria-live="polite">
          {attached.length
            ? t('pages.tasks.smart.attached', {
                names: attached.map((item) => item.label).join(', '),
              })
            : ''}
        </L.SrOnly>
      </M.SmartRow>
    );
  },
);

TaskSmartLinkChips.displayName = 'TaskSmartLinkChips';

interface ITaskSmartLinkSuggestProps {
  title: string;
  task: IStaffTask;
  onAddLinks: (taskId: string, links: TaskLinkInput[]) => Promise<boolean>;
}

/**
 * Подсказка «Связать с …» в окне СУЩЕСТВУЮЩЕЙ задачи (ТЗ 0109).
 *
 * Только по правке названия и НИКОГДА сама: у задачи уже есть связи, которые
 * кто-то выбирал, и молча добавлять к ним новое — значит менять чужую работу.
 * Пока название не тронуто, сервер не спрашиваем вовсе.
 */
export const TaskSmartLinkSuggest = memo(
  ({ title, task, onAddLinks }: ITaskSmartLinkSuggestProps) => {
    const { t } = useTranslation();
    const edited = title.trim() !== task.title.trim();
    const { links: suggestions } = useTaskSmartLinks(title, edited);
    const [busy, setBusy] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    const add = useCallback(
      async (s: ITaskSmartLink) => {
        setBusy(keyOf(s));
        setFailed(false);
        const ok = await onAddLinks(task.id, [{ kind: s.kind, id: s.id }]);
        setBusy(null);
        if (!ok) setFailed(true);
      },
      [onAddLinks, task.id],
    );

    const linked = new Set(
      (task.links || []).map((l) => keyOf({ kind: l.kind, id: l.objectId })),
    );
    const open = edited ? suggestions.filter((s) => !linked.has(keyOf(s))) : [];
    if (!open.length && !failed) return null;

    return (
      <M.SmartRow>
        {open.map((s) => {
          const name = s.hint ? `${s.label} · ${s.hint}` : s.label;
          return (
            <M.SmartChip
              key={keyOf(s)}
              title={`${t(`pages.tasks.links.kind.${s.kind}`)}: ${name}`}
            >
              <M.SmartButton
                type="button"
                disabled={busy !== null}
                aria-label={t('pages.tasks.smart.linkWith', { name })}
                onClick={() => add(s)}
              >
                <Plus size={12} aria-hidden="true" />
                {t('pages.tasks.smart.linkWithShort')}
                <SuggestionBody link={s} />
              </M.SmartButton>
            </M.SmartChip>
          );
        })}
        {failed ? (
          <M.FieldHint role="alert">
            {t('pages.tasks.smart.failed')}
          </M.FieldHint>
        ) : null}
      </M.SmartRow>
    );
  },
);

TaskSmartLinkSuggest.displayName = 'TaskSmartLinkSuggest';
